import { useState, memo, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, PhoneCall } from "lucide-react";
import { useActiveSection } from "@/hooks/useActiveSection";
import { cn } from "@/lib/utils";
import { trackPhoneClick, trackCTAClick } from "@/lib/analytics";
import bearLogo from "@/assets/ceg-mark.png.asset.json";

const RETELL_PHONE_NUMBER = "+15592398181";

// Streamlined nav with direct links (no dropdown) - order matches homepage section flow
const navLinks = [
  { name: "Home", href: "/#home", sectionId: "#home" },
  { name: "PPA", href: "/power-purchase-agreement", isPage: true },
  { name: "Commercial", href: "/commercial", isPage: true },
  { name: "Service Areas", href: "/service-areas", isPage: true },
  { name: "Blog", href: "/blog", isPage: true },
  { name: "FAQ", href: "/faq", isPage: true },
];

// Memoized nav link component for performance
const NavItem = memo(({ 
  link, 
  isActive, 
  href,
  onClick 
}: { 
  link: typeof navLinks[0];
  isActive: boolean;
  href: string;
  onClick?: () => void;
}) => (
  <Link
    to={href}
    className={cn(
      "text-sm font-medium transition-colors relative",
      isActive
        ? "text-primary"
        : "text-foreground/80 hover:text-primary"
    )}
    onClick={onClick}
  >
    {link.name}
    {isActive && (
      <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary rounded-full" />
    )}
  </Link>
));
NavItem.displayName = "NavItem";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === "/";

  const sectionIds = navLinks.filter(link => link.sectionId).map((link) => link.sectionId!);
  const activeSection = useActiveSection(sectionIds);

  const handleCallClick = useCallback(() => {
    trackPhoneClick({
      phone_number: RETELL_PHONE_NUMBER,
      source: "header_cta",
    });
    trackCTAClick({
      button_text: "Start Saving",
      button_location: "header",
    });
    window.location.href = `tel:${RETELL_PHONE_NUMBER}`;
  }, []);

  const getHref = useCallback((link: { href: string; isPage?: boolean; sectionId?: string }) => {
    if (link.isPage) return link.href;
    // For section links, use hash only when on homepage, full path otherwise
    if (link.sectionId && isHomePage) return link.sectionId;
    return link.href;
  }, [isHomePage]);

  const isActive = useCallback((link: typeof navLinks[0]) => link.sectionId ? activeSection === link.sectionId : false, [activeSection]);

  const closeMenu = useCallback(() => setIsMenuOpen(false), []);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-md border-b border-border shadow-sm">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 leading-tight">
            <img
              src={bearLogo.url}
              alt="California Energy Group bear logo"
              className="h-9 md:h-11 w-auto shrink-0"
              width={64}
              height={64}
            />
            <span className="font-display text-lg md:text-xl font-bold text-primary tracking-tight">
              California <span className="text-secondary">Energy</span> Group
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-5 mr-4">
            {navLinks.map((link) => (
              <NavItem
                key={link.name}
                link={link}
                isActive={isActive(link)}
                href={getHref(link)}
              />
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden md:flex items-center">
            <Button 
              onClick={handleCallClick}
              className="bg-gradient-cta hover:opacity-90 text-accent-foreground font-semibold shadow-lg"
            >
              <PhoneCall className="w-4 h-4 mr-2" />
              Start Saving
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-3 -mr-2 touch-manipulation active:scale-95 transition-transform"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu - Simple CSS transition instead of Framer Motion */}
      <div 
        className={cn(
          "md:hidden bg-card border-t border-border overflow-hidden transition-all duration-300 ease-out",
          isMenuOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
        )}
      >
        <nav className="container mx-auto px-4 py-4 flex flex-col gap-1">
          {navLinks.map((link) => {
            // For section links when not on homepage, force full navigation with native anchor
            if (!link.isPage && link.sectionId && !isHomePage) {
              return (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={() => {
                    closeMenu();
                    // Let native anchor behavior handle navigation
                  }}
                  className="block transition-colors py-3 px-3 -mx-1 rounded-lg touch-manipulation text-foreground/80 hover:text-primary active:bg-muted"
                >
                  {link.name}
                </a>
              );
            }
            
            // For page links or section links when on homepage
            return (
              <Link
                key={link.name}
                to={link.isPage ? link.href : (isHomePage ? link.sectionId! : link.href)}
                className={cn(
                  "block transition-colors py-3 px-3 -mx-1 rounded-lg touch-manipulation",
                  isActive(link)
                    ? "text-primary bg-primary/10 font-medium"
                    : "text-foreground/80 hover:text-primary active:bg-muted"
                )}
                onClick={closeMenu}
              >
                {link.name}
              </Link>
            );
          })}
          
          <div className="flex items-center justify-end mt-3 pt-3 border-t border-border">
            <Button 
              onClick={() => {
                closeMenu();
                handleCallClick();
              }}
              className="bg-gradient-cta text-accent-foreground font-semibold h-12 px-6 text-base touch-manipulation active:scale-[0.98] transition-transform"
            >
              <PhoneCall className="w-5 h-5 mr-2" />
              Start Saving
            </Button>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default memo(Header);
